// Белезите „сторнирано с едно действие" — скобата отляво и чипът „×N заедно".
//
// Едно натискане на „Сторнирай всичко" сваля няколко реда от ЕДНА сметка. В
// списъка те стоят един под друг и се четат като няколко отделни решения — а
// са едно. Затова партидата се рисува като блок с четири съгласувани белега:
// общ фон на редовете (тона го дава справката през `rowTone`), плътна скоба,
// която се затваря отгоре и отдолу, чип „×N заедно" на първия ред и
// притъмняло име на масата на следващите — името се изписва веднъж, останалите
// редове са продължение на същата сметка.
//
// Разкъса ли се партидата (друга подредба, филтър, друга страница), скобата
// пада и остава само чипът: скоба между редове, които не са съседни, би
// свързала чужди сторна.

import { useTranslation } from "react-i18next";

import type { BatchPosition } from "./cancellationsMath";

type MarkerProps = {
  position: BatchPosition;
  /** Колко реда има в партидата — за подсказката на скобата. */
  size: number;
};

/** Плътната скоба вляво: `⌐` на първия ред, `│` в средата, `└` на последния. */
export const BatchMarker = ({ position, size }: MarkerProps) => {
  const { t } = useTranslation();

  if (position === "single") {
    // Заема същото място, за да не подскачат имената на масите.
    return <span aria-hidden className="w-[14px] shrink-0" />;
  }

  const hint = t("reportsPage.voidsBatchHint", { count: size });

  // Скобата се дърпа до горния и долния ръб на РЕДА (клетката е `h-full`), за
  // да няма прекъсване между два съседни реда на партидата. Отстъпът от 6px
  // на първия и последния ред е „рамото" на скобата — там тя се затваря.
  const verticalLine =
    position === "first"
      ? "top-1.5 bottom-0"
      : position === "last"
        ? "top-0 bottom-1.5"
        : "inset-y-0";

  return (
    <span className="relative flex w-[14px] shrink-0 self-stretch" title={hint}>
      <span className="sr-only">{hint}</span>
      {position === "lone" ? null : (
        <>
          <span
            aria-hidden
            className={`absolute left-1 w-[2px] rounded-full bg-brand-deep-ink ${verticalLine}`}
          />
          {position === "middle" ? null : (
            <span
              aria-hidden
              className={`absolute left-1 h-[2px] w-[8px] rounded-full bg-brand-deep-ink ${
                position === "first" ? "top-1.5" : "bottom-1.5"
              }`}
            />
          )}
        </>
      )}
    </span>
  );
};

type BadgeProps = { size: number };

/**
 * „×4 заедно" — стои на ПЪРВИЯ видим ред на партидата и казва колко реда е
 * отнесло едно натискане. Дребното „×4" отпреди се губеше до името на масата.
 */
export const BatchBadge = ({ size }: BadgeProps) => {
  const { t } = useTranslation();

  return (
    <span
      // Пълното изречение стои в подсказката — чипът е тесен и казва само броя.
      title={t("reportsPage.voidsBatchHint", { count: size })}
      className="inline-flex w-fit max-w-full items-center truncate rounded-full bg-brand-tint2 px-1.5 py-[1px] font-pos-mono text-[10px] font-bold leading-[1.35] text-brand-deep-ink"
    >
      {t("reportsPage.voidsBatchChip", { count: size })}
    </span>
  );
};
